import * as React from 'react';
import styled, { ThemeContext, css } from 'styled-components';
import { ColorSet, PaletteGroups, getColorSet } from '--components/theme';
import Color from 'color';
import { Strings } from '--client/../strings/en';
import { IndeterminateProgress } from '../Progress';


// Todo:    Maybe add indeterminate progress spinner to buttons and a pending / progressing state


const DEFAULT_BUTTON_HEIGHT = {
    small: "18px",
    medium: "32px",
    large: "48px"
};
const ICON_SIZE_PX = 20;
const ICON_SIZE = `${ICON_SIZE_PX}px`;

export type ButtonStyleProps = {
    colorSet: ColorSet
    fullWidth?: boolean
    size?: "medium" | "large" | "small"
}
type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
    color?: ColorSet | keyof PaletteGroups | "link"
    variant?: "solid" | "outline" | "text"
    size?: "medium" | "large" | "small"
    fullWidth?: boolean
    startIcon?: React.ReactElement
    endIcon?: React.ReactElement
    indeterminate?: boolean;
}

export const buttonBaseStyles = css<ButtonStyleProps>`
    /* Button Style Resets */    
    border: none;
    cursor: pointer;  
    outline: none;

    /* Styles */
    padding: ${props => props.size == "large" ? props.theme.spacing(0, 4) : props.size == "medium" ? props.theme.spacing(0, 2) : props.theme.spacing(0, 1)};
    height: ${props => DEFAULT_BUTTON_HEIGHT[props.size || "medium"]};  
    border-radius: ${props => props.theme.radius.small};
    transition: background ${props => props.theme.transitions.toggle};

    /* Mixins */
    ${props => (
        props.size === "small"
            ? props.theme.typography.body3
            : props.theme.typography.body2
    )}
    margin-bottom:0;

    /* Align icons */
    vertical-align: middle;
    > span {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    }
`;
const SolidButton = styled.button<ButtonStyleProps>`
    /* Base */
    ${buttonBaseStyles}

    /* Width */
    align-self:${props => (!props.fullWidth) ? 'initial' : 'stretch'};
    width:${props => props.fullWidth ? '100%' : undefined};

    /* Color */
    box-shadow: ${props => props.theme.shadows.inset};
    background: ${props => props.colorSet.main};
    color: ${props => props.colorSet.contrast};
    :hover:not(:disabled) {
         background:${props => props.colorSet.lighter}
    }
    :active:not(:disabled) {
        background:${props => props.colorSet.darker}
    }
    :focus:not(:disabled) {
        box-shadow: ${props => props.theme.shadows.inset}, 0 0 4px 0 ${props => Color(props.colorSet.main).alpha(0.75).toString()};
    }
    :disabled {
        opacity: 0.5;
        cursor: default;
    } 
`;
const OutlineButton = styled.button<ButtonStyleProps>`
    /* Base */
    ${buttonBaseStyles}

    /* Width */
    align-self:${props => (!props.fullWidth) ? 'initial' : 'stretch'};
    width:${props => props.fullWidth ? '100%' : undefined};

    /* Color */
    background: transparent;
    color: ${props => props.theme.palette.text.primary};
    border: 1px solid ${props => props.colorSet.main};
    svg {
        color: ${props => props.colorSet.main};
    }
    :hover:not(:disabled) {
        background:${props => Color(props.colorSet.main).alpha(0.1).toString()};
        border: 1px solid ${props => props.colorSet.darker};
        svg {
            color: ${props => props.colorSet.darker};
        }
    }
    :active:not(:disabled) {
        background:${props => Color(props.colorSet.main).alpha(0.2).toString()};
        border: 1px solid ${props => props.colorSet.darker};
        svg {
            color: ${props => props.colorSet.darker};
        }
    }
    :focus:not(:disabled):not(:hover):not(:active) {
        background:${props => Color(props.colorSet.main).alpha(0.1).toString()};
    }
    :disabled {
        opacity: 0.5;
        cursor: default;
    } 
`;
const TextButton = styled.button<ButtonStyleProps>`
    /* Base */
    ${buttonBaseStyles}

    /* Width */
    align-self:${props => (!props.fullWidth) ? 'initial' : 'stretch'};
    width:${props => props.fullWidth ? '100%' : undefined};

    background: none;
    color: ${props => props.colorSet.main};
    :focus:not(:disabled),
    :hover:not(:disabled) {
        background:${props => Color(props.colorSet.lighter).alpha(0.1).toString()};
    }
    :active:not(:disabled) {
        background:${props => Color(props.colorSet.lighter).alpha(0.15).toString()};
    }
    :disabled {
        opacity: 0.5;
        cursor: default;
    } 
`;
const StartIcon = styled.div`
    display: flex;
    align-items: center;
    margin: ${props => props.theme.spacing(0, 0.5, 0, 0)};
`;
const EndIcon = styled.div`
    display: flex;
    align-items: center;
    margin: ${props => props.theme.spacing(0, 0, 0, 0.5)};
`;

export const OKButton = React.forwardRef(
    (props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
        return <Button ref={ref} {...props}>
            <Strings.Inline>
                OK
            </Strings.Inline>
        </Button>
    }
);
export const CancelButton = React.forwardRef(
    (props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
        return <Button ref={ref} {...props}>
            <Strings.Inline>
                Cancel
            </Strings.Inline>
        </Button>
    }
);

const ButtonInterdeterminateProgressWrapper = styled.div`
    align-items: center;
    display: flex;
    height: ${ICON_SIZE};
    margin: ${props => props.theme.spacing(0, 0.5, 0, 0)};
    width: ${ICON_SIZE};
`;

const Button = React.forwardRef((props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
    const { color, variant, children, startIcon: startIconProp, endIcon: endIconProp, indeterminate, ...buttonProps } = props
    const theme = React.useContext(ThemeContext);

    let Component;
    if (variant == "outline") Component = OutlineButton;
    else if (variant == "text") Component = TextButton;
    else Component = SolidButton;

    const colorSet = getColorSet(color, theme);
    const startIcon = startIconProp && React.cloneElement(startIconProp, { size: ICON_SIZE });
    const endIcon = endIconProp && React.cloneElement(endIconProp, { size: ICON_SIZE });

    return <Component colorSet={colorSet} ref={ref} {...buttonProps} type={props.type || "button"}>
        <span>
            {indeterminate
                ? (
                    <ButtonInterdeterminateProgressWrapper>
                        <IndeterminateProgress
                            size={16}
                            color={
                                variant === "text"
                                    ? colorSet.main
                                    : variant === "outline"
                                        ? colorSet.contrast
                                        : theme.palette.text.primary}
                        />
                    </ButtonInterdeterminateProgressWrapper>
                )
                : startIcon && <StartIcon>{startIcon}</StartIcon>}
            {children}
            {endIcon && <EndIcon>{endIcon}</EndIcon>}
        </span>
    </Component>
})

export default Button;
export { Button, ButtonProps }